// Builds an animated XP reveal for reward screens.
// Animates through every level boundary: bar fills, resets at level-up, repeats.

import { CHAMPIONS } from "../data/champions";
import { championIconUrl } from "../config/constants";
import { levelForXp, XP_FOR_LEVEL, MAX_LEVEL } from "../units/UnitStats";
import type { RosterEntry } from "../game/RunState";

const SEGMENT_MS = 600;       // fill animation duration per level segment
const LEVEL_UP_PAUSE_MS = 250; // extra pause after each level-up before next segment
const UNIT_STAGGER_MS = 130;   // delay between unit start times

interface Segment {
  startFrac: number;   // where white bar starts (0–1) within the level
  endFrac: number;     // where white bar ends (0–1)
  levelAtStart: number;
  isLevelUp: boolean;  // true when this segment fills to 100% (triggers reset)
}

function computeSegments(preXp: number, postXp: number): Segment[] {
  const segs: Segment[] = [];
  let cur = preXp;

  while (cur < postXp) {
    const level = levelForXp(cur);

    if (level >= MAX_LEVEL) {
      segs.push({ startFrac: 0, endFrac: 1, levelAtStart: level, isLevelUp: false });
      break;
    }

    const floor = XP_FOR_LEVEL[level - 1];
    const ceil  = XP_FOR_LEVEL[level];
    const range = ceil - floor;
    const startFrac = (cur - floor) / range;

    if (postXp >= ceil) {
      segs.push({ startFrac, endFrac: 1, levelAtStart: level, isLevelUp: true });
      cur = ceil;
    } else {
      segs.push({ startFrac, endFrac: (postXp - floor) / range, levelAtStart: level, isLevelUp: false });
      cur = postXp;
    }
  }

  return segs;
}

export function buildXpReveal(pre: RosterEntry[], post: RosterEntry[]): HTMLElement {
  const list = document.createElement("div");
  list.className = "xpr-list";

  type UnitAnim = {
    oldFill: HTMLDivElement;
    newFill: HTMLDivElement;
    levelEl: HTMLSpanElement;
    badge: HTMLSpanElement;
    segments: Segment[];
  };
  const unitAnims: UnitAnim[] = [];

  for (const postEntry of post) {
    const preEntry = pre.find((r) => r.id === postEntry.id);
    if (!preEntry) continue;

    const champ    = CHAMPIONS[postEntry.championId];
    const preLevel = levelForXp(preEntry.xp);

    // ---- DOM ----
    const card = document.createElement("div");
    card.className = "xpr-unit";

    const iconEl = document.createElement("div");
    iconEl.className = "xpr-icon";
    iconEl.style.backgroundImage = `url('${championIconUrl(champ.iconId)}')`;

    const info    = document.createElement("div");
    info.className = "xpr-info";

    const nameRow = document.createElement("div");
    nameRow.className = "xpr-name-row";

    const nameEl  = document.createElement("span");
    nameEl.className = "xpr-name";
    nameEl.textContent = champ.displayName;

    const levelEl = document.createElement("span");
    levelEl.className = "xpr-level";
    levelEl.textContent = `Lv ${preLevel}`;

    const badge = document.createElement("span");
    badge.className = "xpr-badge";
    badge.style.display = "none";

    nameRow.append(nameEl, levelEl, badge);

    const track   = document.createElement("div");
    track.className = "xpr-bar";

    const oldFill = document.createElement("div");
    oldFill.className = "xpr-bar-old";

    const newFill = document.createElement("div");
    newFill.className = "xpr-bar-new";
    newFill.style.transition = "none";

    track.append(oldFill, newFill);
    info.append(nameRow, track);
    card.append(iconEl, info);
    list.appendChild(card);

    const segments = computeSegments(preEntry.xp, postEntry.xp);

    if (segments.length === 0) {
      // No XP gained — show static current state
      if (preLevel >= MAX_LEVEL) {
        oldFill.style.width = "100%";
      } else {
        const floor = XP_FOR_LEVEL[preLevel - 1];
        const ceil  = XP_FOR_LEVEL[preLevel];
        oldFill.style.width = `${((preEntry.xp - floor) / (ceil - floor)) * 100}%`;
      }
      levelEl.textContent = `Lv ${preLevel}`;
      continue;
    }

    // Set initial visual state before animation triggers
    const first = segments[0];
    oldFill.style.width = `${first.startFrac * 100}%`;
    newFill.style.left  = `${first.startFrac * 100}%`;
    newFill.style.width = "0";

    unitAnims.push({ oldFill, newFill, levelEl, badge, segments });
  }

  // Kick off staggered per-unit animations after two RAFs (lets DOM paint first)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      unitAnims.forEach(({ oldFill, newFill, levelEl, badge, segments }, unitIdx) => {
        let levelsGained = 0;
        let segDelay = unitIdx * UNIT_STAGGER_MS;

        segments.forEach((seg, segIdx) => {
          const delay = segDelay;

          setTimeout(() => {
            if (segIdx > 0) {
              // Coming off a level-up: clear purple, reset white fill to left edge
              oldFill.style.width = "0";
              newFill.style.transition = "none";
              newFill.style.left  = "0";
              newFill.style.width = "0";
            }

            // Two RAFs so the "transition: none" reset is committed before we re-enable
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                newFill.style.transition = `width ${SEGMENT_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
                newFill.style.width = `${(seg.endFrac - seg.startFrac) * 100}%`;
              });
            });

            if (seg.isLevelUp) {
              // Update level and badge after the fill completes
              setTimeout(() => {
                levelsGained++;
                levelEl.textContent = `Lv ${seg.levelAtStart + 1}`;
                badge.style.display = "";
                badge.textContent   = `+${levelsGained}`;
              }, SEGMENT_MS + 60);
            }
          }, delay);

          segDelay += SEGMENT_MS + (seg.isLevelUp ? LEVEL_UP_PAUSE_MS : 0);
        });
      });
    });
  });

  return list;
}
